import "./Callout.scss";

import React, { useLayoutEffect, useState } from "react";

import tada from "../assets/tada.png";
import useMergedRefs from "./hooks/useMergedRefs";
import Markdown from "./Markdown";

type Props = {
  children: string;
  width: number;
  x: number;
  y: number;
  isEmphasized?: boolean;
  isCelebration?: boolean;
  tail?: { x: number; y: number };
};

export default React.forwardRef(function Callout(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
) {
  const { children, width, x, y, isEmphasized, isCelebration } = props;

  const [calloutHeight, setCalloutHeight] = useState<number | null>(null);
  const [calloutElement, setCalloutElement] = useState<HTMLDivElement | null>(
    null,
  );
  const mergedRef = useMergedRefs(ref, setCalloutElement);

  useLayoutEffect(
    () => setCalloutHeight(calloutElement ? calloutElement.offsetHeight : null),
    [calloutElement, children, width],
  );

  let tailNode: React.ReactNode | null = null;
  if (props.tail !== undefined) {
    const { x: tailX, y: tailY } = props.tail;

    let style: React.CSSProperties;
    let className = "Tail ";
    if (tailX < x) {
      style = {
        left: tailX - x,
        top: Math.max(0, tailY - y),
        width: Math.abs(x - tailX),
      };
      className += "Horizontal";
    } else if (tailX > x && tailX < x + width) {
      style = {
        left: tailX - x,
        top: Math.min(0, tailY - y),
        height: Math.abs(tailY - y),
      };
      className += "Vertical";
    } else {
      // diagonal line
      // we'll position the head at the callout, then rotate about the top left by the correct angle
      if (calloutHeight === null) {
        style = {
          display: "none",
        };
      } else {
        const cornerRadius = 4;
        let baseX = x + width - cornerRadius;
        const baseY =
          tailY > y + calloutHeight
            ? y + calloutHeight - cornerRadius
            : tailY < y
            ? y + cornerRadius
            : y + calloutHeight / 2.0;
        let dx = tailX - baseX;
        const dy = tailY - baseY;
        while (Math.PI / 2.0 - Math.atan2(dy, dx) < 0.5) {
          baseX--;
          dx++;
        }

        style = {
          left: baseX - x,
          top: baseY - y,
          width: Math.sqrt(dx * dx + dy * dy),
          transformOrigin: "top left",
          transform: `rotate(${Math.atan2(dy, dx)}rad)`,
          zIndex: -1,
        };
      }
      className += "Horizontal";
    }

    tailNode = <div className={className} style={style} />;
  }

  return (
    <div
      className={`Callout ${isEmphasized ? "Emphasized" : ""} ${
        isCelebration ? "Celebration" : ""
      }`}
      style={{ width, left: x, top: y }}
      ref={mergedRef}
    >
      {tailNode}
      {isCelebration && <img src={tada} alt="" />}
      <Markdown>{children}</Markdown>
    </div>
  );
});
